import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Megaphone, Plus, Trash2, Edit2, X, Save, AlertCircle, Filter,
  Trophy, Heart, HelpCircle, Bell, EyeOff, MessageCircle, Send,
} from "lucide-react";
import { API, useAuth } from "@/contexts/AuthContext";

// ───────────────────────────────────────────────────────────────
// Post type definitions, easy to tweak labels/icons later
// ───────────────────────────────────────────────────────────────

export const POST_TYPES = [
  { value: "achievement", label: "Achievement", icon: Trophy, color: "#EA823A", bg: "#FCEADB",
    placeholder: "Tell the team about a win, a milestone reached, a goal hit, an outcome to celebrate." },
  { value: "good_news", label: "Good news", icon: Heart, color: "#E2718A", bg: "#FCE7EB",
    placeholder: "A meaningful moment, a kind email, a story worth sharing." },
  { value: "ask", label: "Ask for help", icon: HelpCircle, color: "#284952", bg: "#DDE7E9",
    placeholder: "Need a second opinion, a referral, or cover? Be specific so a teammate can step in." },
  { value: "heads_up", label: "Heads up", icon: Bell, color: "#1F6B3A", bg: "#DCEFE0",
    placeholder: "FYI for the team, schedule changes, partner updates, anything we should know." },
];

const TYPE_BY_VALUE = Object.fromEntries(POST_TYPES.map((t) => [t.value, t]));

export const ALLOWED_REACTIONS = ["🎉", "❤️", "👍", "🙌", "💡", "🤝"];

function relativeTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return date.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
}

// ───────────────────────────────────────────────────────────────
// Main page
// ───────────────────────────────────────────────────────────────

export default function Noticeboard() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = filter === "all" ? `${API}/noticeboard` : `${API}/noticeboard?type_filter=${filter}`;
      const { data } = await axios.get(url);
      setPosts(data || []);
    } catch (e) {
      console.error("Failed to load posts:", e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="noticeboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <div className="text-sm uppercase tracking-wider text-[#EA823A] font-semibold mb-2 flex items-center gap-2">
            <Megaphone className="w-4 h-4" /> Team noticeboard
          </div>
          <h1 className="font-display text-4xl text-[#1F2A2E]">What&apos;s happening</h1>
          <p className="text-[#4A595E] mt-2">Wins, good news stories and asks for help, just between the team.</p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary" data-testid="new-post-btn">
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="noticeboard-filters">
        <FilterPill active={filter === "all"} onClick={() => setFilter("all")} label="All" testId="filter-all" />
        {POST_TYPES.map((t) => (
          <FilterPill
            key={t.value}
            active={filter === t.value}
            onClick={() => setFilter(t.value)}
            label={t.label}
            icon={t.icon}
            color={t.color}
            testId={`filter-${t.value}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20"><div className="w-8 h-8 border-3 border-[#E2718A] border-t-transparent rounded-full animate-spin mx-auto" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-3xl border border-[#F0E5DE]" data-testid="noticeboard-empty">
          <Megaphone className="w-12 h-12 mx-auto text-[#F4C3CC] mb-4" />
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2">Nothing here yet</h3>
          <p className="text-[#6B7A80] text-sm mb-6">Be the first to share a win, a story, or ask the team for help.</p>
          <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-primary text-sm">Share something</button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="noticeboard-posts">
          {posts.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              onReact={async (emoji) => {
                const { data } = await axios.post(`${API}/noticeboard/${p.id}/react`, { emoji });
                setPosts((prev) => prev.map((x) => (x.id === p.id ? data : x)));
              }}
              onUpdate={(updated) => {
                setPosts((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
              }}
              onEdit={() => { setEditing(p); setShowForm(true); }}
              onDelete={async () => {
                if (!window.confirm("Delete this post? This cannot be undone.")) return;
                await axios.delete(`${API}/noticeboard/${p.id}`);
                load();
              }}
            />
          ))}
        </div>
      )}

      {showForm && (
        <PostFormModal
          post={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Post card
// ───────────────────────────────────────────────────────────────

export function PostCard({ post, onReact, onEdit, onDelete, onUpdate, compact = false }) {
  const { user } = useAuth();
  const t = TYPE_BY_VALUE[post.type] || POST_TYPES[1];
  const Icon = t.icon;
  const canEdit = user && (user.role === "admin" || user.id === post.author_id);
  const isAnonAuthor = post.anonymous;
  const comments = post.comments || [];

  return (
    <article
      className="bg-white rounded-3xl border border-[#F0E5DE] p-6 hover:shadow-sm transition group"
      data-testid={`post-${post.id}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{ background: t.bg, color: t.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <span className="text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full" style={{ background: t.bg, color: t.color }}>
              {t.label}
            </span>
            <div className="flex items-center gap-2 text-xs text-[#6B7A80]">
              <span className="flex items-center gap-1">
                {isAnonAuthor && <EyeOff className="w-3 h-3" />}
                <span>{post.author_name || "Anonymous teammate"}</span>
              </span>
              <span>·</span>
              <span>{relativeTime(post.created_at)}</span>
              {canEdit && !compact && (
                <>
                  <button onClick={onEdit} className="text-[#6B7A80] hover:text-[#E2718A] ml-2 opacity-0 group-hover:opacity-100 transition" data-testid={`edit-post-${post.id}`} aria-label="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={onDelete} className="text-[#6B7A80] hover:text-red-500 opacity-0 group-hover:opacity-100 transition" data-testid={`delete-post-${post.id}`} aria-label="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )}
            </div>
          </div>
          <h3 className="font-display text-xl text-[#1F2A2E] mb-2 leading-snug">{post.title}</h3>
          <p className={`text-[#284952] leading-relaxed whitespace-pre-line ${compact ? "text-sm line-clamp-2" : ""}`}>
            {post.body}
          </p>

          {!compact && (
            <>
              <ReactionBar reactions={post.reactions || {}} onReact={onReact} commentCount={comments.length} />
              <CommentsSection post={post} comments={comments} onUpdate={onUpdate} />
            </>
          )}
        </div>
      </div>
    </article>
  );
}

function ReactionBar({ reactions, onReact, commentCount = 0 }) {
  const { user } = useAuth();
  const sortedEmojis = ALLOWED_REACTIONS;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-[#F0E5DE]" data-testid="reaction-bar">
      {sortedEmojis.map((e) => {
        const userIds = reactions[e] || [];
        const count = userIds.length;
        const youReacted = user && userIds.includes(user.id);
        return (
          <button
            key={e}
            onClick={() => onReact(e)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-sm transition ${
              youReacted ? "bg-[#FCE7EB] border-[#E2718A] text-[#E2718A]" : "bg-white border-[#F0E5DE] text-[#284952] hover:bg-[#F1E4D5]"
            }`}
            data-testid={`react-${e}`}
            title={`React with ${e}`}
          >
            <span className="text-base leading-none">{e}</span>
            {count > 0 && <span className="text-xs font-semibold tabular-nums">{count}</span>}
          </button>
        );
      })}
      {commentCount > 0 && (
        <span className="ml-auto text-xs text-[#6B7A80] flex items-center gap-1.5">
          <MessageCircle className="w-3.5 h-3.5" />
          <span className="tabular-nums">{commentCount}</span>
          <span className="hidden sm:inline">{commentCount === 1 ? "reply" : "replies"}</span>
        </span>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Comments
// ───────────────────────────────────────────────────────────────

function CommentsSection({ post, comments, onUpdate }) {
  const { user } = useAuth();
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(false);

  const visibleComments = expanded ? comments : comments.slice(-2);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/noticeboard/${post.id}/comments`, { body: body.trim() });
      onUpdate?.(data);
      setBody("");
      setExpanded(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't post reply.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (commentId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      const { data } = await axios.delete(`${API}/noticeboard/${post.id}/comments/${commentId}`);
      onUpdate?.(data);
    } catch (err) {
      alert(err?.response?.data?.detail || "Couldn't delete reply.");
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-[#F0E5DE] space-y-3" data-testid={`comments-${post.id}`}>
      {comments.length > 2 && !expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs font-semibold text-[#E2718A] hover:text-[#284952]"
          data-testid={`comments-show-all-${post.id}`}
        >
          Show all {comments.length} replies
        </button>
      )}

      {visibleComments.map((c) => {
        const canDelete = user && (user.role === "admin" || user.id === c.author_id);
        return (
          <div key={c.id} className="bg-[#F1E4D5] rounded-2xl px-4 py-3 flex items-start gap-3 group/comment" data-testid={`comment-${c.id}`}>
            <div className="w-7 h-7 rounded-full bg-[#DDE7E9] text-[#284952] flex items-center justify-center text-xs font-semibold shrink-0">
              {(c.author_name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-[#1F2A2E] truncate">{c.author_name || "Teammate"}</span>
                  <span className="text-[#6B7A80]">·</span>
                  <span className="text-[#6B7A80]">{relativeTime(c.created_at)}</span>
                </div>
                {canDelete && (
                  <button
                    onClick={() => remove(c.id)}
                    className="text-[#6B7A80] hover:text-red-500 opacity-0 group-hover/comment:opacity-100 transition"
                    aria-label="Delete comment"
                    data-testid={`delete-comment-${c.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <p className="text-sm text-[#284952] whitespace-pre-line leading-relaxed">{c.body}</p>
            </div>
          </div>
        );
      })}

      <form onSubmit={submit} className="flex items-start gap-2" data-testid={`comment-form-${post.id}`}>
        <textarea
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={1000}
          className="input-navica flex-1 resize-none text-sm"
          placeholder="Reply to the team..."
          data-testid={`comment-input-${post.id}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submit(e);
          }}
        />
        <button
          type="submit"
          disabled={saving || !body.trim()}
          className="btn-primary !px-3 !py-2.5 shrink-0"
          aria-label="Post reply"
          data-testid={`comment-submit-${post.id}`}
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
      {error && (
        <div className="text-xs text-red-600 flex gap-1.5 items-center"><AlertCircle className="w-3 h-3" />{error}</div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// New / Edit post modal
// ───────────────────────────────────────────────────────────────

function PostFormModal({ post, onClose, onSaved }) {
  const isEdit = !!post?.id;
  const [type, setType] = useState(post?.type || "good_news");
  const [title, setTitle] = useState(post?.title || "");
  const [body, setBody] = useState(post?.body || "");
  const [anonymous, setAnonymous] = useState(post?.anonymous || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const selectedType = TYPE_BY_VALUE[type] || POST_TYPES[1];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { type, title, body, anonymous };
      if (isEdit) {
        await axios.put(`${API}/noticeboard/${post.id}`, payload);
      } else {
        await axios.post(`${API}/noticeboard`, payload);
      }
      onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Couldn't save post.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="post-form-modal">
      <div className="bg-white rounded-[2rem] w-full max-w-xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-[#F0E5DE]">
          <h2 className="font-display text-2xl text-[#1F2A2E]">{isEdit ? "Edit post" : "New post"}</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-[#F1E4D5] flex items-center justify-center" aria-label="Close" data-testid="post-form-close">
            <X className="w-5 h-5 text-[#6B7A80]" />
          </button>
        </div>

        <form onSubmit={submit} className="p-7 space-y-5">
          {/* Type selector */}
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">Post type</label>
            <div className="grid grid-cols-2 gap-2">
              {POST_TYPES.map((t) => {
                const Icon = t.icon;
                const active = type === t.value;
                return (
                  <button
                    type="button"
                    key={t.value}
                    onClick={() => setType(t.value)}
                    className={`flex items-center gap-2.5 px-3 py-3 rounded-xl border text-sm font-medium text-left transition ${
                      active ? "border-[#E2718A]" : "border-[#F0E5DE] hover:border-[#F4C3CC]"
                    }`}
                    style={active ? { background: t.bg } : {}}
                    data-testid={`type-${t.value}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" style={{ color: t.color }} />
                    <span className="text-[#1F2A2E]">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Title</label>
            <input
              className="input-navica"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              required
              placeholder="Short and clear..."
              data-testid="post-title"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">Details</label>
            <textarea
              rows={6}
              className="input-navica resize-none"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={selectedType.placeholder}
              required
              data-testid="post-body"
            />
            <div className="text-xs text-[#6B7A80] mt-1.5 flex justify-between">
              <span>Visible only to the Navica team. Do NOT include identifying client details.</span>
              <span className="tabular-nums">{body.length}/4000</span>
            </div>
          </div>

          {/* Anonymous toggle - only meaningful for asks */}
          {(type === "ask" || anonymous) && (
            <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl bg-[#F1E4D5] border border-[#F0E5DE]">
              <input
                type="checkbox"
                checked={anonymous}
                onChange={(e) => setAnonymous(e.target.checked)}
                className="navica-check mt-0.5"
                data-testid="post-anonymous"
              />
              <span className="text-sm text-[#284952]">
                <span className="font-semibold flex items-center gap-1.5"><EyeOff className="w-3.5 h-3.5" /> Post anonymously</span>
                <span className="block text-xs text-[#6B7A80] mt-0.5">Your teammates won&apos;t see your name. The Director can still see who you are for safety/escalation purposes.</span>
              </span>
            </label>
          )}

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2 items-start text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#F0E5DE]">
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
            <button type="submit" disabled={saving || !title.trim() || !body.trim()} className="btn-primary" data-testid="post-submit">
              {saving ? "Posting..." : isEdit ? "Save changes" : "Post"} <Save className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, label, icon: Icon, color, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
        active ? "bg-[#1F2A2E] text-white" : "bg-white border border-[#F0E5DE] text-[#284952] hover:bg-[#F1E4D5]"
      }`}
    >
      {Icon && <Icon className="w-4 h-4" style={!active && color ? { color } : {}} />}
      {label}
    </button>
  );
}
